import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, ShoppingBag, ShoppingCart, Coins, Clock, Users, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { StatCard, PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { listProducts } from '@/features/products/products.api';
import { useWarehouses } from '@/features/inventory/inventory.api';
import { usePurchases, useCreatePurchase, type PurchaseLineInput } from './purchases.api';

const IGV = 0.18;
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n: number) => 'S/ ' + n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PurchasesPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = usePurchases();
  const items = data?.items ?? [];

  const kpis = useMemo(() => {
    const totalSum = items.reduce((s, p) => s + Number(p.total), 0);
    const credito = items.filter((p) => p.paymentTerm === 'CREDITO').length;
    const proveedores = new Set(items.map((p) => p.supplierDoc)).size;
    return { totalSum, credito, proveedores, orders: items.length };
  }, [items]);

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Compras"
          sub="Facturas de proveedores · ingresan stock y generan cuentas por pagar"
          actions={
            <button className="btn btn-primary" onClick={() => setOpen(true)}>
              <Plus size={17} />
              Nueva compra
            </button>
          }
        />

        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Total compras" value={fmtMoney(kpis.totalSum)} icon={<ShoppingCart size={19} />} accent="#8B5CF6" sub="cargadas" />
          <StatCard label="Órdenes" value={String(kpis.orders)} icon={<Coins size={19} />} accent="#3B82F6" />
          <StatCard label="A crédito" value={String(kpis.credito)} icon={<Clock size={19} />} accent="#F59E0B" />
          <StatCard label="Proveedores" value={String(kpis.proveedores)} icon={<Users size={19} />} accent="#22C55E" />
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Historial de compras</div>
            <span className="t-sm muted">{items.length} órdenes</span>
          </div>
          {isLoading ? (
            <div style={{ padding: '8px 0' }}>
              {Array.from({ length: 5 }).map((_, r) => (
                <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  {Array.from({ length: 5 }).map((_, c) => (
                    <div key={c} className="sk" style={{ height: 12, flex: c === 1 ? 2 : 1, opacity: 1 - r * 0.1 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <ShoppingBag size={28} />
              </div>
              <div>
                <div className="t-h2" style={{ marginBottom: 4 }}>
                  Sin compras
                </div>
                <div className="muted">Registra tu primera compra a proveedores.</div>
              </div>
              <button className="btn btn-primary" onClick={() => setOpen(true)}>
                <Plus size={17} />
                Nueva compra
              </button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Proveedor</th>
                    <th>Comprobante</th>
                    <th>Condición</th>
                    <th className="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id}>
                      <td className="tnum">
                        <DateDisplay value={p.issueDate} />
                      </td>
                      <td>
                        <div className="strong">{p.supplierName}</div>
                        <div className="t-xs muted tnum">{p.supplierDoc}</div>
                      </td>
                      <td className="tnum">
                        {p.docType} {p.supplierDocNumber ?? ''}
                      </td>
                      <td>
                        <span className={`badge ${p.paymentTerm === 'CREDITO' ? 'badge-amber' : 'badge-green'}`}>
                          <span className="bdot" />
                          {p.paymentTerm === 'CREDITO' ? 'Crédito' : 'Contado'}
                        </span>
                      </td>
                      <td className="right tnum strong">
                        <Money value={p.total} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {open && <NewPurchaseDialog onClose={() => setOpen(false)} />}
    </div>
  );
}

function NewPurchaseDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreatePurchase();
  const { data: products } = useQuery({ queryKey: ['products-for-purchase'], queryFn: () => listProducts({ pageSize: 200 }) });
  const warehouses = useWarehouses(); // 403 si no hay módulo inventario → data undefined

  const [h, setH] = useState({
    supplierName: '',
    supplierDoc: '',
    docType: 'FACTURA' as 'FACTURA' | 'BOLETA' | 'RECIBO' | 'OTRO',
    supplierDocNumber: '',
    issueDate: todayISO(),
    paymentTerm: 'CONTADO' as 'CONTADO' | 'CREDITO',
    dueDate: '',
    warehouseId: '',
  });
  const [lines, setLines] = useState<PurchaseLineInput[]>([
    { productId: null, description: '', quantity: 1, unitCost: 0, taxCategory: 'GRAVADO' },
  ]);

  const totals = useMemo(() => {
    let sub = 0, igv = 0;
    for (const l of lines) {
      const net = (Number(l.quantity) || 0) * (Number(l.unitCost) || 0);
      sub += net;
      if (l.taxCategory === 'GRAVADO') igv += net * IGV;
    }
    return { sub, igv, total: sub + igv };
  }, [lines]);

  const setLine = (i: number, patch: Partial<PurchaseLineInput>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((p) => [...p, { productId: null, description: '', quantity: 1, unitCost: 0, taxCategory: 'GRAVADO' }]);
  const removeLine = (i: number) => setLines((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const onPickProduct = (i: number, productId: string) => {
    const p = products?.items.find((x) => x.id === productId);
    if (p) setLine(i, { productId: p.id, description: p.name, unitCost: Number(p.price), taxCategory: p.taxCategory });
    else setLine(i, { productId: null });
  };

  const valid = h.supplierName.trim() && h.supplierDoc.trim() && lines.every((l) => l.description.trim() && l.quantity > 0);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        supplierName: h.supplierName.trim(),
        supplierDoc: h.supplierDoc.trim(),
        docType: h.docType,
        supplierDocNumber: h.supplierDocNumber.trim() || undefined,
        issueDate: h.issueDate,
        paymentTerm: h.paymentTerm,
        dueDate: h.paymentTerm === 'CREDITO' && h.dueDate ? h.dueDate : undefined,
        warehouseId: h.warehouseId || undefined,
        lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity), unitCost: Number(l.unitCost) })),
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <CxDialog
      title="Nueva compra"
      sub="Registra una factura de proveedor"
      icon={<ShoppingCart size={20} />}
      iconTone="primary"
      width={640}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="pur-form" disabled={!valid || create.isPending}>
            <Check size={18} />
            {create.isPending ? 'Guardando…' : 'Registrar compra'}
          </button>
        </>
      }
    >
      <form id="pur-form" onSubmit={submit} className="col gap14">
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Proveedor (razón social)</label>
            <input className="input" value={h.supplierName} required onChange={(e) => setH((s) => ({ ...s, supplierName: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">RUC / Doc.</label>
            <input className="input tnum" value={h.supplierDoc} required onChange={(e) => setH((s) => ({ ...s, supplierDoc: e.target.value }))} />
          </div>
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div className="field">
            <label className="label">Tipo</label>
            <select className="select" value={h.docType} onChange={(e) => setH((s) => ({ ...s, docType: e.target.value as typeof h.docType }))}>
              <option value="FACTURA">Factura</option>
              <option value="BOLETA">Boleta</option>
              <option value="RECIBO">Recibo</option>
              <option value="OTRO">Otro</option>
            </select>
          </div>
          <div className="field">
            <label className="label">N° comprobante</label>
            <input className="input tnum" value={h.supplierDocNumber} placeholder="F001-123" onChange={(e) => setH((s) => ({ ...s, supplierDocNumber: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">Fecha</label>
            <input className="input" type="date" value={h.issueDate} required onChange={(e) => setH((s) => ({ ...s, issueDate: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">Condición</label>
            <div className="seg" style={{ width: '100%' }}>
              <button type="button" className={h.paymentTerm === 'CONTADO' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setH((s) => ({ ...s, paymentTerm: 'CONTADO' }))}>
                Contado
              </button>
              <button type="button" className={h.paymentTerm === 'CREDITO' ? 'on' : ''} style={{ flex: 1 }} onClick={() => setH((s) => ({ ...s, paymentTerm: 'CREDITO' }))}>
                Crédito
              </button>
            </div>
          </div>
        </div>
        {(h.paymentTerm === 'CREDITO' || (warehouses.data?.length ?? 0) > 0) && (
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {h.paymentTerm === 'CREDITO' && (
              <div className="field">
                <label className="label">Vence</label>
                <input className="input" type="date" value={h.dueDate} onChange={(e) => setH((s) => ({ ...s, dueDate: e.target.value }))} />
              </div>
            )}
            {(warehouses.data?.length ?? 0) > 0 && (
              <div className="field">
                <label className="label">Almacén (ingresa stock)</label>
                <select className="select" value={h.warehouseId} onChange={(e) => setH((s) => ({ ...s, warehouseId: e.target.value }))}>
                  <option value="">— No ingresar stock —</option>
                  {(warehouses.data ?? []).map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} · {w.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            Productos
          </div>
          <div className="card">
            {lines.map((l, i) => (
              <div key={i} className="grid aic" style={{ gridTemplateColumns: '1fr 70px 96px 34px', gap: 8, padding: '10px 12px', borderBottom: i < lines.length - 1 ? '1px solid #F4F6F9' : 'none' }}>
                <div className="col gap6">
                  <select className="select" style={{ height: 34 }} value={l.productId ?? ''} onChange={(e) => onPickProduct(i, e.target.value)}>
                    <option value="">— Libre —</option>
                    {(products?.items ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} · {p.name}
                      </option>
                    ))}
                  </select>
                  <input className="input" style={{ height: 34 }} value={l.description} placeholder="Descripción" onChange={(e) => setLine(i, { description: e.target.value })} />
                </div>
                <input className="input tnum" style={{ height: 34, textAlign: 'right' }} type="number" step="any" min="0" value={l.quantity} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} />
                <input className="input tnum" style={{ height: 34, textAlign: 'right' }} type="number" step="any" min="0" value={l.unitCost} onChange={(e) => setLine(i, { unitCost: Number(e.target.value) })} />
                <button type="button" className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--error)' }} onClick={() => removeLine(i)}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" style={{ margin: 8 }} onClick={addLine}>
              <Plus size={15} />
              Agregar línea
            </button>
          </div>
        </div>

        <div className="card card-pad" style={{ background: 'var(--canvas)' }}>
          <div className="col gap8">
            <div className="row between t-sm">
              <span className="muted">Subtotal</span>
              <span className="tnum">
                <Money value={totals.sub.toFixed(2)} />
              </span>
            </div>
            <div className="row between t-sm">
              <span className="muted">IGV (18%)</span>
              <span className="tnum">
                <Money value={totals.igv.toFixed(2)} />
              </span>
            </div>
            <div className="row between aic" style={{ paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
              <span className="fw7">Total</span>
              <span className="tnum fw8" style={{ fontSize: 18 }}>
                <Money value={totals.total.toFixed(2)} />
              </span>
            </div>
          </div>
        </div>
      </form>
    </CxDialog>
  );
}
